/**
 * Admin — Events / Venues migration review.
 *
 * Phase 3 of the events/venues rewrite deliberately stopped short of changing
 * anything that was ambiguous. It left three piles of work for a human:
 *
 *   1. 7 venue names in a MANUAL REVIEW bucket (parks, civic halls, a postal
 *      address typed into a venue field, empty venues, one unidentifiable string)
 *   2. 10 miscategorised events, logged but NOT applied
 *   3. duplicate / recurrence groups, flagged but NOT merged
 *
 * This screen surfaces that curated data straight from
 * scripts/data/events-venue-model-mapping.json (via the API — it is never
 * recomputed here) joined onto the live rows, and lets an admin resolve each
 * item individually. Nothing on this page is bulk and nothing is automatic.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { Link, Redirect } from "wouter";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import {
  AlertTriangle, CheckCircle2, XCircle, GitMerge, ExternalLink,
  RotateCcw, Info, MapPin, Link2, ArrowRight,
} from "lucide-react";

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

// ─── Types mirroring GET /api/admin/venue-model/review ────────────────────────

interface LiveEvent {
  id: number;
  name?: string;
  slug?: string | null;
  category?: string;
  town?: string;
  venue?: string | null;
  venueId?: number | null;
  isVenueEvent?: boolean;
  eventDate?: string;
  eventTime?: string | null;
  status?: string;
  approved?: boolean;
  imageUrl?: string | null;
  description?: string;
  missing?: boolean;
}

interface Resolution {
  status: string;
  action?: string;
  venueId?: number | null;
  category?: string | null;
  note?: string | null;
  by: number;
  at: string;
}

interface VenueLite {
  id: number;
  name: string;
  slug: string | null;
  town: string | null;
  venueType: string;
  isActive: boolean;
  approved: boolean;
  businessId: number | null;
  createdBy: number | null;
}

interface ManualReviewItem {
  key: string;
  venueText: string;
  reviewBucket: string;
  reason: string | null;
  recommendation: string | null;
  flags: string[];
  source: string;
  perEvent: Record<string, string>;
  events: LiveEvent[];
  resolution: Resolution | null;
}

interface CorrectionItem {
  eventId: number;
  title: string;
  current: string;
  proposed: string;
  confidence: string;
  evidence: string;
  liveCategory: string | null;
  liveName: string | null;
  liveStatus: string | null;
  missing: boolean;
  applied: boolean;
  resolution: Resolution | null;
}

interface DuplicateGroup {
  key: string;
  id: string;
  kind: string;
  venue: string;
  venueId: number | null;
  eventIds: number[];
  finding?: string;
  caveat?: string;
  action?: string;
  detail?: Record<string, unknown>[];
  events: LiveEvent[];
  resolution: Resolution | null;
}

interface VenueDuplicate {
  key: string;
  normalisedName: string;
  venues: VenueLite[];
}

interface EventLink {
  eventId: number;
  suggestVenueSlug: string;
  confidence: string;
  basis: string;
  liveEvent: LiveEvent | null;
  targetVenue: VenueLite | null;
  applied: boolean;
  resolution: Resolution | null;
}

interface ReviewPayload {
  mapping: { version: string | null; generatedFor: string | null; auditBaselineDate: string | null; sourcePath: string };
  manualReview: ManualReviewItem[];
  corrections: CorrectionItem[];
  duplicateGroups: DuplicateGroup[];
  venueDuplicates: VenueDuplicate[];
  eventLinks: EventLink[];
  dataIntegrityFlags: { severity: string; flag: string; detail: string }[];
  recommendationsNotApplied: string[];
  unmappedFrontendTokens: { token: string; source: string; status: string; suggestion: string; note: string }[];
  canonicalCategories: { name: string; categoryId: number; duplicateIds: number[]; note?: string }[];
  venues: VenueLite[];
  merges: { kind: string; sourceId: number; targetId: number; by: number; at: string; moved: Record<string, number> }[];
}

type Tab = "review" | "corrections" | "duplicates" | "links" | "flags";

const BUCKET_LABEL: Record<string, string> = {
  "civic-venue": "Civic venue",
  "public-space-keep-as-free-text": "Public space",
  "data-quality-defect": "Data defect",
  "no-venue-information": "No venue info",
  unidentifiable: "Unidentifiable",
};

const CONFIDENCE_STYLE: Record<string, string> = {
  high: "bg-emerald-50 text-emerald-700 border-emerald-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  low: "bg-slate-50 text-slate-600 border-slate-200",
};

const SEVERITY_STYLE: Record<string, string> = {
  high: "bg-red-50 text-red-700 border-red-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  low: "bg-slate-50 text-slate-600 border-slate-200",
};

function Pill({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full border ${className}`}>
      {children}
    </span>
  );
}

function EventCard({ e, highlight = false }: { e: LiveEvent; highlight?: boolean }) {
  if (e.missing) {
    return (
      <div className="rounded-lg border border-dashed border-slate-200 p-3 text-xs text-slate-400">
        Event #{e.id} no longer exists in the database.
      </div>
    );
  }
  return (
    <div className={`rounded-lg border p-3 ${highlight ? "border-primary/40 bg-primary/5" : "border-slate-200 bg-white"}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm text-slate-800 truncate">
            #{e.id} {e.name}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5 flex flex-wrap gap-x-2">
            <span>{e.eventDate}</span>
            {e.eventTime && <span>· {e.eventTime.trim()}</span>}
            <span>· {e.town}</span>
            <span>· {e.category}</span>
          </div>
          <div className="text-[11px] text-slate-400 mt-0.5">
            venue: {e.venue?.trim() ? `"${e.venue}"` : <em>empty</em>}
            {e.venueId ? ` · venue_id ${e.venueId}` : " · venue_id NULL"}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Pill className={e.status === "approved" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-orange-50 text-orange-700 border-orange-200"}>
            {e.status}
          </Pill>
          {e.status !== "approved" && e.approved && (
            <Pill className="bg-red-50 text-red-700 border-red-200" >status/approved split</Pill>
          )}
        </div>
      </div>
      {e.description && <p className="text-[11px] text-slate-500 mt-1.5 line-clamp-2">{e.description}</p>}
    </div>
  );
}

// ─── Confirmable merge dialog ─────────────────────────────────────────────────

interface MergePlan {
  kind: "event" | "venue";
  sourceId: number;
  targetId: number;
  sourceLabel: string;
  targetLabel: string;
}

function MergeConfirm({ plan, onDone, onCancel }: {
  plan: MergePlan;
  onDone: () => void;
  onCancel: () => void;
}) {
  const { toast } = useToast();
  const [preview, setPreview] = useState<Record<string, unknown> | null>(null);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (plan.kind !== "venue") return;
    void (async () => {
      try {
        const r = await fetch(
          `/api/admin/venue-model/venues/merge-preview?sourceId=${plan.sourceId}&targetId=${plan.targetId}`,
          { headers: authHeaders() },
        );
        if (r.ok) setPreview(await r.json() as Record<string, unknown>);
      } catch { /* preview is best-effort */ }
    })();
  }, [plan]);

  const run = async () => {
    setBusy(true);
    try {
      const url = plan.kind === "venue"
        ? "/api/admin/venue-model/venues/merge"
        : "/api/admin/venue-model/events/merge";
      const r = await fetch(url, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ sourceId: plan.sourceId, targetId: plan.targetId, confirm: true }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({})) as { error?: string };
        throw new Error(d.error ?? "Merge failed");
      }
      toast({ title: `Merged #${plan.sourceId} into #${plan.targetId}` });
      onDone();
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : "Merge failed", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const willMove = (preview?.["willMove"] ?? null) as Record<string, number> | null;
  const copied = (preview?.["fieldsCopiedIfTargetBlank"] ?? null) as string[] | null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 pt-10 pb-8 px-4">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
          <GitMerge size={18} className="text-primary" />
          <h2 className="text-lg font-bold text-slate-800">Confirm {plan.kind} merge</h2>
        </div>

        <div className="p-6 space-y-4">
          <div className="rounded-xl border border-slate-200 p-3 text-sm">
            <div className="text-xs font-bold uppercase text-slate-400 tracking-wider mb-2">This is what happens</div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-red-600">#{plan.sourceId} {plan.sourceLabel}</span>
              <ArrowRight size={14} className="text-slate-400" />
              <span className="font-medium text-emerald-700">#{plan.targetId} {plan.targetLabel}</span>
            </div>
            <ul className="mt-3 text-xs text-slate-600 list-disc pl-4 space-y-1">
              {plan.kind === "venue" ? (
                <>
                  <li>Events, facilities, activities, specials, sports listings and followers are repointed at the survivor.</li>
                  <li>Blank fields on the survivor are filled from the loser. Amenities and photos are unioned. Ownership is inherited if the survivor has none.</li>
                  <li>The loser is <strong>not deleted</strong> — it is set inactive + unapproved and tagged <code>[MERGED into venue #{plan.targetId}]</code>.</li>
                </>
              ) : (
                <>
                  <li>Media and saves move to the survivor; the loser&apos;s image is kept as an extra media row.</li>
                  <li>Blank fields on the survivor are filled from the loser.</li>
                  <li>The loser is <strong>not deleted</strong> — it is set to <code>cancelled</code> with a pointer to the survivor.</li>
                </>
              )}
            </ul>
          </div>

          {plan.kind === "venue" && willMove && (
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="text-xs font-bold uppercase text-slate-400 tracking-wider mb-2">Preview</div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-600">
                {Object.entries(willMove).map(([k, v]) => (
                  <div key={k} className="flex justify-between">
                    <span className="capitalize">{k.replace(/([A-Z])/g, " $1").toLowerCase()}</span>
                    <span className="font-bold text-slate-800">{v}</span>
                  </div>
                ))}
              </div>
              {copied && copied.length > 0 && (
                <div className="mt-2 text-[11px] text-slate-500">
                  Fields copied to survivor: {copied.join(", ")}
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">
              Type <code className="font-mono text-slate-800">MERGE</code> to confirm
            </label>
            <Input value={typed} onChange={(e) => setTyped(e.target.value)} className="h-9" placeholder="MERGE" />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-200 bg-slate-50">
          <button onClick={onCancel} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800">Cancel</button>
          <button
            onClick={() => void run()}
            disabled={busy || typed !== "MERGE"}
            className="px-5 py-2 text-sm font-bold text-white bg-primary rounded-xl hover:bg-primary/90 disabled:opacity-40"
          >
            {busy ? "Merging…" : "Merge"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AdminVenueReview() {
  const { user, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const [tab, setTab] = useState<Tab>("review");
  const [data, setData] = useState<ReviewPayload | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [mergePlan, setMergePlan] = useState<MergePlan | null>(null);
  const [matchChoice, setMatchChoice] = useState<Record<string, string>>({});
  const [mergePick, setMergePick] = useState<Record<string, { source?: number; target?: number }>>({});
  // Per-bucket event selection. A bucket can hold unrelated events (the
  // empty-venue bucket is three different events in three different places),
  // so the admin must be able to link a subset. Undefined = all selected.
  const [eventPick, setEventPick] = useState<Record<string, number[]>>({});

  const selectedEventIds = useCallback(
    (item: ManualReviewItem) => eventPick[item.key] ?? item.events.filter((e) => !e.missing).map((e) => e.id),
    [eventPick],
  );

  const toggleEventPick = useCallback((item: ManualReviewItem, id: number) => {
    setEventPick((p) => {
      const current = p[item.key] ?? item.events.filter((e) => !e.missing).map((e) => e.id);
      const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
      return { ...p, [item.key]: next };
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch("/api/admin/venue-model/review", { headers: authHeaders() });
      if (!r.ok) {
        const d = await r.json().catch(() => ({})) as { error?: string; detail?: string };
        setLoadError(d.detail ?? d.error ?? "Failed to load the review queue");
        setData(null);
        return;
      }
      setLoadError(null);
      setData(await r.json() as ReviewPayload);
    } catch {
      setLoadError("Failed to load the review queue");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const post = useCallback(async (key: string, url: string, body: unknown, okMessage: string) => {
    setBusyKey(key);
    try {
      const r = await fetch(url, { method: "POST", headers: authHeaders(), body: JSON.stringify(body) });
      if (!r.ok) {
        const d = await r.json().catch(() => ({})) as { error?: string };
        throw new Error(d.error ?? "Request failed");
      }
      toast({ title: okMessage });
      await load();
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : "Request failed", variant: "destructive" });
    } finally {
      setBusyKey(null);
    }
  }, [load, toast]);

  const counts = useMemo(() => {
    if (!data) return { review: 0, corrections: 0, duplicates: 0, links: 0, flags: 0 };
    return {
      review: data.manualReview.filter((m) => m.resolution?.status !== "resolved").length,
      corrections: data.corrections.filter((c) => !c.applied && c.resolution?.status !== "rejected").length,
      duplicates: data.duplicateGroups.filter((g) => !g.resolution && g.kind !== "NOT_DUPLICATE_VERIFIED").length
        + data.venueDuplicates.length,
      links: data.eventLinks.filter((l) => !l.applied && l.resolution?.status !== "dismissed").length,
      flags: data.dataIntegrityFlags.length,
    };
  }, [data]);

  if (authLoading) return null;
  if (user?.role !== "admin") return <Redirect to="/" />;

  const TABS: { key: Tab; label: string; count: number }[] = [
    { key: "review",      label: "Manual review",        count: counts.review },
    { key: "corrections", label: "Category corrections", count: counts.corrections },
    { key: "duplicates",  label: "Duplicates & merges",  count: counts.duplicates },
    { key: "links",       label: "Suggested links",      count: counts.links },
    { key: "flags",       label: "Data flags",           count: counts.flags },
  ];

  return (
    <AdminLayout current="/admin/venue-review">
      {mergePlan && (
        <MergeConfirm
          plan={mergePlan}
          onCancel={() => setMergePlan(null)}
          onDone={() => { setMergePlan(null); void load(); }}
        />
      )}

      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-primary">Events / Venues Review</h1>
          <p className="text-sm text-slate-500 mt-1">
            Everything the Phase 3 migration deliberately did <em>not</em> decide. Each item is resolved
            individually — nothing here is bulk and no merge happens without confirmation.
          </p>
          {data && (
            <p className="text-[11px] text-slate-400 mt-1 font-mono">
              mapping v{data.mapping.version ?? "?"} · baseline {data.mapping.auditBaselineDate ?? "?"} · {data.mapping.sourcePath}
            </p>
          )}
        </div>

        {loadError && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            <div className="font-bold flex items-center gap-2"><AlertTriangle size={15} /> Could not load the review queue</div>
            <p className="mt-1 text-xs">{loadError}</p>
          </div>
        )}

        <div className="flex gap-1 border-b border-slate-200 flex-wrap">
          {TABS.map(({ key, label, count }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
                tab === key ? "border-primary text-primary" : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
              }`}
            >
              {label}
              {count > 0 && (
                <span className="bg-orange-100 text-orange-700 text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                  {count}
                </span>
              )}
            </button>
          ))}
        </div>

        {loading && <div className="text-slate-400 text-sm py-8 text-center">Loading review queue…</div>}

        {/* ── MANUAL REVIEW ─────────────────────────────────────────────────── */}
        {!loading && data && tab === "review" && (
          <div className="space-y-4">
            <p className="text-xs text-slate-500">
              {data.manualReview.length} venue identities Phase 3 refused to guess. A park is not a
              venue — &ldquo;keep as free text&rdquo; is a legitimate answer and changes no data.
            </p>
            {data.manualReview.map((item) => {
              const resolved = item.resolution?.status === "resolved";
              const partial = item.resolution?.status === "partial";
              const choice = matchChoice[item.key] ?? "";
              const picked = selectedEventIds(item);
              const multi = item.events.filter((e) => !e.missing).length > 1;
              return (
                <div key={item.key} className={`rounded-2xl border p-4 ${resolved ? "border-emerald-200 bg-emerald-50/40" : partial ? "border-amber-200 bg-amber-50/30" : "border-slate-200 bg-white"}`}>
                  <div className="flex items-start gap-3 flex-wrap">
                    <div className="flex-1 min-w-[240px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <MapPin size={14} className="text-slate-400" />
                        <span className="font-bold text-slate-800">
                          {item.venueText.trim() ? `"${item.venueText}"` : <em className="text-slate-400">(empty venue field)</em>}
                        </span>
                        <Pill className="bg-slate-50 text-slate-600 border-slate-200">
                          {BUCKET_LABEL[item.reviewBucket] ?? item.reviewBucket}
                        </Pill>
                        <Pill className="bg-slate-50 text-slate-500 border-slate-200">{item.source}</Pill>
                      </div>
                      {item.reason && <p className="text-xs text-slate-600 mt-2">{item.reason}</p>}
                      {item.recommendation && (
                        <p className="text-xs text-slate-500 mt-1.5">
                          <strong className="text-slate-600">Phase 3 recommends:</strong> {item.recommendation}
                        </p>
                      )}
                      {item.flags.length > 0 && (
                        <ul className="mt-1.5 text-[11px] text-amber-700 list-disc pl-4 space-y-0.5">
                          {item.flags.map((f, i) => <li key={i}>{f}</li>)}
                        </ul>
                      )}
                    </div>
                    {item.resolution && (
                      <div className="text-right">
                        <Pill className={resolved ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}>
                          <CheckCircle2 size={11} /> {partial ? "partly " : ""}{item.resolution.action?.replace(/_/g, " ")}
                        </Pill>
                        <div className="text-[10px] text-slate-400 mt-1">
                          {item.resolution.at ? new Date(item.resolution.at).toLocaleString("en-GB") : ""}
                        </div>
                      </div>
                    )}
                  </div>

                  {item.events.length > 0 && (
                    <>
                      {multi && !resolved && (
                        <p className="text-[11px] text-slate-500 mt-3">
                          Tick only the events that belong at the same place — these were bucketed by a
                          shared defect, not by a shared location.
                        </p>
                      )}
                      <div className="mt-2 grid gap-2 sm:grid-cols-2">
                        {item.events.map((e) => (
                          <div key={e.id}>
                            {multi && !resolved && !e.missing && (
                              <label className="flex items-center gap-1.5 text-[11px] text-slate-600 mb-1 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={picked.includes(e.id)}
                                  onChange={() => toggleEventPick(item, e.id)}
                                  className="rounded"
                                />
                                Include event #{e.id}
                              </label>
                            )}
                            <EventCard e={e} highlight={multi && picked.includes(e.id)} />
                            {item.perEvent[String(e.id)] && (
                              <p className="text-[11px] text-slate-500 mt-1 pl-1">{item.perEvent[String(e.id)]}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  <div className="mt-3 pt-3 border-t border-slate-100 flex items-end gap-2 flex-wrap">
                    {item.resolution && (
                      <button
                        onClick={() => void post(item.key, `/api/admin/venue-model/review/${item.key}`, { action: "reopen" }, "Reopened")}
                        disabled={busyKey === item.key}
                        className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:border-primary/40 hover:text-primary disabled:opacity-50"
                      >
                        <RotateCcw size={12} /> Reopen
                      </button>
                    )}
                    {!resolved && (
                      <>
                        <div>
                          <label className="block text-[11px] font-semibold text-slate-500 mb-1">Match to an existing venue</label>
                          <select
                            value={choice}
                            onChange={(e) => setMatchChoice((p) => ({ ...p, [item.key]: e.target.value }))}
                            className="h-8 text-xs border border-slate-200 rounded-lg px-2 bg-white min-w-[220px]"
                          >
                            <option value="">Select a venue…</option>
                            {data.venues.map((v) => (
                              <option key={v.id} value={String(v.id)}>
                                #{v.id} {v.name}{v.town ? ` — ${v.town}` : ""}{v.isActive ? "" : " (inactive)"}
                              </option>
                            ))}
                          </select>
                        </div>
                        <button
                          onClick={() => void post(
                            item.key,
                            `/api/admin/venue-model/review/${item.key}`,
                            { action: "match_existing", venueId: Number(choice), linkEvents: picked.length > 0, eventIds: picked },
                            `Linked ${picked.length} event(s)`,
                          )}
                          disabled={busyKey === item.key || !choice}
                          className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-lg border border-primary/30 text-primary hover:bg-primary/5 disabled:opacity-40"
                        >
                          <Link2 size={12} /> Match &amp; link {picked.length > 0 ? `(${picked.length})` : ""}
                        </button>
                        <button
                          onClick={() => {
                            if (!window.confirm(`Create a new staged venue for "${item.venueText}"?\n\nIt will be created inactive + unapproved, invisible to the public until you approve it on the Venues screen.${picked.length ? `\n\n${picked.length} event(s) will be linked to it.` : ""}`)) return;
                            void post(
                              item.key,
                              `/api/admin/venue-model/review/${item.key}`,
                              { action: "create_venue", linkEvents: picked.length > 0, eventIds: picked },
                              "Venue created (staged, unapproved)",
                            );
                          }}
                          disabled={busyKey === item.key || !item.venueText.trim()}
                          title={item.venueText.trim() ? "" : "This bucket has no venue name to create from — match to an existing venue instead."}
                          className="text-xs font-bold px-3 py-1.5 rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40"
                        >
                          Create venue
                        </button>
                        <button
                          onClick={() => void post(
                            item.key,
                            `/api/admin/venue-model/review/${item.key}`,
                            { action: "keep_free_text" },
                            "Kept as free text — no data changed",
                          )}
                          disabled={busyKey === item.key}
                          className="text-xs font-medium px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:border-slate-300 disabled:opacity-50"
                        >
                          Keep as free text
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── CORRECTIONS ───────────────────────────────────────────────────── */}
        {!loading && data && tab === "corrections" && (
          <div className="space-y-3">
            <p className="text-xs text-slate-500">
              {data.corrections.length} miscategorisations logged by Phase 3 and never applied.
              Apply or reject each one on its own evidence. Two of them are genuinely two-valued —
              you can override the target category before applying.
            </p>
            {data.corrections.map((c) => {
              const key = `corr-${c.eventId}`;
              const rejected = c.resolution?.status === "rejected";
              return (
                <div
                  key={c.eventId}
                  className={`rounded-2xl border p-4 ${
                    c.applied ? "border-emerald-200 bg-emerald-50/40"
                    : rejected ? "border-slate-200 bg-slate-50"
                    : "border-slate-200 bg-white"
                  }`}
                >
                  <div className="flex items-start gap-3 flex-wrap">
                    <div className="flex-1 min-w-[240px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-slate-800 text-sm">#{c.eventId} {c.liveName ?? c.title}</span>
                        <Pill className={CONFIDENCE_STYLE[c.confidence] ?? CONFIDENCE_STYLE["low"]!}>
                          {c.confidence} confidence
                        </Pill>
                        {c.missing && <Pill className="bg-red-50 text-red-700 border-red-200">event missing</Pill>}
                      </div>
                      <div className="flex items-center gap-2 mt-2 text-sm flex-wrap">
                        <span className="px-2 py-0.5 rounded-lg bg-slate-100 text-slate-600 text-xs font-mono">
                          {c.liveCategory ?? c.current}
                        </span>
                        <ArrowRight size={13} className="text-slate-400" />
                        <span className="px-2 py-0.5 rounded-lg bg-primary/10 text-primary text-xs font-mono font-bold">
                          {c.proposed}
                        </span>
                        {c.liveCategory !== null && c.liveCategory !== c.current && !c.applied && (
                          <span className="text-[11px] text-amber-600">
                            (live value has drifted from the logged &ldquo;{c.current}&rdquo;)
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-600 mt-2">{c.evidence}</p>
                    </div>
                    <div className="text-right">
                      {c.applied && <Pill className="bg-emerald-50 text-emerald-700 border-emerald-200"><CheckCircle2 size={11} /> applied</Pill>}
                      {rejected && !c.applied && <Pill className="bg-slate-100 text-slate-600 border-slate-200"><XCircle size={11} /> rejected</Pill>}
                    </div>
                  </div>

                  <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 flex-wrap">
                    {!c.applied && (
                      <>
                        <button
                          onClick={() => void post(key, `/api/admin/venue-model/corrections/${c.eventId}`, { action: "apply" }, `Event ${c.eventId} → ${c.proposed}`)}
                          disabled={busyKey === key || c.missing}
                          className="text-xs font-bold px-3 py-1.5 rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40"
                        >
                          Apply → {c.proposed}
                        </button>
                        <select
                          defaultValue=""
                          onChange={(e) => {
                            const v = e.target.value;
                            e.currentTarget.value = "";
                            if (!v) return;
                            if (!window.confirm(`Set event #${c.eventId} category to "${v}" instead of the proposed "${c.proposed}"?`)) return;
                            void post(key, `/api/admin/venue-model/corrections/${c.eventId}`, { action: "apply", category: v }, `Event ${c.eventId} → ${v}`);
                          }}
                          disabled={busyKey === key || c.missing}
                          className="h-7 text-xs border border-slate-200 rounded-lg px-2 bg-white disabled:opacity-40"
                        >
                          <option value="">Apply a different category…</option>
                          {data.canonicalCategories.map((cat) => (
                            <option key={cat.categoryId} value={cat.name}>{cat.name}</option>
                          ))}
                        </select>
                      </>
                    )}
                    {!rejected && !c.applied && (
                      <button
                        onClick={() => void post(key, `/api/admin/venue-model/corrections/${c.eventId}`, { action: "reject" }, "Correction rejected — category unchanged")}
                        disabled={busyKey === key}
                        className="text-xs font-medium px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:border-red-300 hover:text-red-600 disabled:opacity-50"
                      >
                        Reject
                      </button>
                    )}
                    {(rejected || c.applied) && (
                      <button
                        onClick={() => void post(key, `/api/admin/venue-model/corrections/${c.eventId}`, { action: "clear" }, "Verdict cleared")}
                        disabled={busyKey === key}
                        className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg border border-slate-200 text-slate-500 hover:border-primary/40 hover:text-primary disabled:opacity-50"
                      >
                        <RotateCcw size={12} /> Clear verdict
                      </button>
                    )}
                    <Link href={`/admin/events`} className="ml-auto inline-flex items-center gap-1 text-xs text-slate-400 hover:text-primary">
                      <ExternalLink size={11} /> Open in Events
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── DUPLICATES ────────────────────────────────────────────────────── */}
        {!loading && data && tab === "duplicates" && (
          <div className="space-y-6">
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 flex gap-2">
              <Info size={14} className="flex-shrink-0 mt-0.5" />
              <span>
                A merge never deletes. Child rows are repointed at the survivor, blank fields are filled
                from the loser, and the loser is soft-flagged with a pointer back to the survivor.
                Every merge is written to the admin audit log.
              </span>
            </div>

            {data.venueDuplicates.length > 0 && (
              <div>
                <h2 className="text-sm font-bold text-slate-700 mb-2">Duplicate venues</h2>
                <div className="space-y-3">
                  {data.venueDuplicates.map((g) => {
                    const pick = mergePick[g.key] ?? {};
                    return (
                      <div key={g.key} className="rounded-2xl border border-slate-200 bg-white p-4">
                        <div className="text-xs text-slate-500 mb-3">
                          {g.venues.length} venues share the normalised name <code className="font-mono">{g.normalisedName}</code>
                        </div>
                        <div className="grid gap-2 sm:grid-cols-2">
                          {g.venues.map((v) => (
                            <div key={v.id} className="rounded-lg border border-slate-200 p-3">
                              <div className="font-semibold text-sm text-slate-800">#{v.id} {v.name}</div>
                              <div className="text-[11px] text-slate-500 mt-0.5">
                                {v.town ?? "—"} · {v.venueType} · {v.isActive ? "live" : "inactive"}{v.approved ? " · approved" : ""}
                              </div>
                              <div className="text-[11px] text-slate-400">
                                business {v.businessId ?? "—"} · manager {v.createdBy ?? "—"}
                              </div>
                              <div className="flex gap-1.5 mt-2">
                                <button
                                  onClick={() => setMergePick((p) => ({ ...p, [g.key]: { ...pick, source: v.id } }))}
                                  className={`text-[11px] font-medium px-2 py-0.5 rounded border ${pick.source === v.id ? "border-red-300 bg-red-50 text-red-700" : "border-slate-200 text-slate-500"}`}
                                >
                                  Merge from
                                </button>
                                <button
                                  onClick={() => setMergePick((p) => ({ ...p, [g.key]: { ...pick, target: v.id } }))}
                                  className={`text-[11px] font-medium px-2 py-0.5 rounded border ${pick.target === v.id ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-500"}`}
                                >
                                  Keep
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="mt-3 pt-3 border-t border-slate-100">
                          <button
                            disabled={!pick.source || !pick.target || pick.source === pick.target}
                            onClick={() => setMergePlan({
                              kind: "venue",
                              sourceId: pick.source!,
                              targetId: pick.target!,
                              sourceLabel: g.venues.find((v) => v.id === pick.source)?.name ?? "",
                              targetLabel: g.venues.find((v) => v.id === pick.target)?.name ?? "",
                            })}
                            className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg border border-primary/30 text-primary hover:bg-primary/5 disabled:opacity-40"
                          >
                            <GitMerge size={12} /> Merge selected…
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <h2 className="text-sm font-bold text-slate-700 mb-2">Flagged event groups</h2>
              <div className="space-y-3">
                {data.duplicateGroups.map((g) => {
                  const pick = mergePick[g.key] ?? {};
                  const mergeable = g.kind === "TRUE_DUPLICATE";
                  return (
                    <div key={g.key} className={`rounded-2xl border p-4 ${g.resolution ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-white"}`}>
                      <div className="flex items-start gap-2 flex-wrap">
                        <div className="flex-1 min-w-[240px]">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold text-slate-800 text-sm">{g.venue}</span>
                            <Pill className={
                              g.kind === "TRUE_DUPLICATE" ? "bg-red-50 text-red-700 border-red-200"
                              : g.kind === "RECURRENCE_CANDIDATE" ? "bg-amber-50 text-amber-700 border-amber-200"
                              : "bg-slate-50 text-slate-600 border-slate-200"
                            }>
                              {g.kind.replace(/_/g, " ").toLowerCase()}
                            </Pill>
                            {g.resolution && (
                              <Pill className="bg-emerald-50 text-emerald-700 border-emerald-200">
                                <CheckCircle2 size={11} /> {g.resolution.status}
                              </Pill>
                            )}
                          </div>
                          {g.finding && <p className="text-xs text-slate-600 mt-2">{g.finding}</p>}
                          {g.caveat && (
                            <p className="text-xs text-amber-700 mt-1.5 flex gap-1.5">
                              <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" /> {g.caveat}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="mt-3 grid gap-2 sm:grid-cols-2">
                        {g.events.map((e) => (
                          <div key={e.id}>
                            <EventCard e={e} highlight={pick.target === e.id} />
                            {mergeable && !e.missing && (
                              <div className="flex gap-1.5 mt-1.5">
                                <button
                                  onClick={() => setMergePick((p) => ({ ...p, [g.key]: { ...pick, source: e.id } }))}
                                  className={`text-[11px] font-medium px-2 py-0.5 rounded border ${pick.source === e.id ? "border-red-300 bg-red-50 text-red-700" : "border-slate-200 text-slate-500"}`}
                                >
                                  Merge from
                                </button>
                                <button
                                  onClick={() => setMergePick((p) => ({ ...p, [g.key]: { ...pick, target: e.id } }))}
                                  className={`text-[11px] font-medium px-2 py-0.5 rounded border ${pick.target === e.id ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-500"}`}
                                >
                                  Keep
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 flex-wrap">
                        {mergeable && (
                          <button
                            disabled={!pick.source || !pick.target || pick.source === pick.target}
                            onClick={() => setMergePlan({
                              kind: "event",
                              sourceId: pick.source!,
                              targetId: pick.target!,
                              sourceLabel: g.events.find((e) => e.id === pick.source)?.name ?? "",
                              targetLabel: g.events.find((e) => e.id === pick.target)?.name ?? "",
                            })}
                            className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg border border-primary/30 text-primary hover:bg-primary/5 disabled:opacity-40"
                          >
                            <GitMerge size={12} /> Merge selected…
                          </button>
                        )}
                        <button
                          onClick={() => void post(g.key, `/api/admin/venue-model/duplicate-groups/${encodeURIComponent(g.key)}`, { status: "not_duplicate" }, "Marked as not a duplicate")}
                          disabled={busyKey === g.key}
                          className="text-xs font-medium px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:border-slate-300 disabled:opacity-50"
                        >
                          Not a duplicate
                        </button>
                        <button
                          onClick={() => void post(g.key, `/api/admin/venue-model/duplicate-groups/${encodeURIComponent(g.key)}`, { status: "deferred" }, "Deferred")}
                          disabled={busyKey === g.key}
                          className="text-xs font-medium px-3 py-1.5 rounded-lg border border-slate-200 text-slate-500 hover:border-slate-300 disabled:opacity-50"
                        >
                          Defer
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {data.merges.length > 0 && (
              <div>
                <h2 className="text-sm font-bold text-slate-700 mb-2">Merge history</h2>
                <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
                  {data.merges.map((m, i) => (
                    <div key={i} className="px-4 py-2 text-xs text-slate-600 flex items-center gap-2 flex-wrap">
                      <Pill className="bg-slate-50 text-slate-600 border-slate-200">{m.kind}</Pill>
                      <span>#{m.sourceId}</span>
                      <ArrowRight size={11} className="text-slate-400" />
                      <span className="font-semibold">#{m.targetId}</span>
                      <span className="text-slate-400">
                        {Object.entries(m.moved).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                      </span>
                      <span className="ml-auto text-slate-400">{new Date(m.at).toLocaleString("en-GB")}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── SUGGESTED LINKS ───────────────────────────────────────────────── */}
        {!loading && data && tab === "links" && (
          <div className="space-y-3">
            <p className="text-xs text-slate-500">
              Event → venue links Phase 3 recorded but did not write, because setting
              <code className="mx-1 font-mono">events.venue_id</code> changes what
              <code className="mx-1 font-mono">/whats-on</code> returns.
            </p>
            {data.eventLinks.map((l) => {
              const key = `link-${l.eventId}`;
              const dismissed = l.resolution?.status === "dismissed";
              return (
                <div key={l.eventId} className={`rounded-2xl border p-4 ${l.applied ? "border-emerald-200 bg-emerald-50/40" : dismissed ? "border-slate-200 bg-slate-50" : "border-slate-200 bg-white"}`}>
                  <div className="flex items-start gap-3 flex-wrap">
                    <div className="flex-1 min-w-[240px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-slate-800 text-sm">
                          #{l.eventId} {l.liveEvent?.name ?? "(event not found)"}
                        </span>
                        <Pill className={CONFIDENCE_STYLE[l.confidence] ?? CONFIDENCE_STYLE["low"]!}>{l.confidence}</Pill>
                        {l.applied && <Pill className="bg-emerald-50 text-emerald-700 border-emerald-200"><CheckCircle2 size={11} /> linked</Pill>}
                        {dismissed && !l.applied && <Pill className="bg-slate-100 text-slate-600 border-slate-200">dismissed</Pill>}
                      </div>
                      <div className="text-xs text-slate-500 mt-1.5">
                        → {l.targetVenue ? `#${l.targetVenue.id} ${l.targetVenue.name}` : <span className="text-amber-700">{l.suggestVenueSlug} (venue does not exist yet)</span>}
                      </div>
                      <p className="text-[11px] text-slate-500 mt-1">{l.basis}</p>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-slate-100 flex gap-2 flex-wrap">
                    <button
                      onClick={() => void post(key, `/api/admin/venue-model/event-links/${l.eventId}`, { action: "apply", venueId: l.targetVenue?.id }, "Event linked to venue")}
                      disabled={busyKey === key || !l.targetVenue || l.applied}
                      className="text-xs font-bold px-3 py-1.5 rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40"
                    >
                      Apply link
                    </button>
                    <button
                      onClick={() => void post(key, `/api/admin/venue-model/event-links/${l.eventId}`, { action: "dismiss" }, "Suggestion dismissed")}
                      disabled={busyKey === key}
                      className="text-xs font-medium px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:border-slate-300 disabled:opacity-50"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── FLAGS ─────────────────────────────────────────────────────────── */}
        {!loading && data && tab === "flags" && (
          <div className="space-y-4">
            <div>
              <h2 className="text-sm font-bold text-slate-700 mb-2">Data integrity flags</h2>
              <div className="space-y-2">
                {data.dataIntegrityFlags.map((f, i) => (
                  <div key={i} className="rounded-xl border border-slate-200 bg-white p-3">
                    <div className="flex items-center gap-2">
                      <Pill className={SEVERITY_STYLE[f.severity] ?? SEVERITY_STYLE["low"]!}>{f.severity}</Pill>
                      <span className="font-semibold text-sm text-slate-800">{f.flag}</span>
                    </div>
                    <p className="text-xs text-slate-600 mt-1.5">{f.detail}</p>
                  </div>
                ))}
              </div>
            </div>

            {data.recommendationsNotApplied.length > 0 && (
              <div>
                <h2 className="text-sm font-bold text-slate-700 mb-2">Recommended but not applied</h2>
                <ul className="rounded-xl border border-slate-200 bg-white p-4 text-xs text-slate-600 list-disc pl-8 space-y-1.5">
                  {data.recommendationsNotApplied.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
            )}

            {data.unmappedFrontendTokens.length > 0 && (
              <div>
                <h2 className="text-sm font-bold text-slate-700 mb-2">Frontend amenity tokens with no live row</h2>
                <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
                  {data.unmappedFrontendTokens.map((t) => (
                    <div key={t.token} className="px-4 py-2.5 text-xs text-slate-600">
                      <code className="font-mono font-bold text-slate-800">{t.token}</code>
                      <span className="text-slate-400"> → suggests </span>
                      <code className="font-mono">{t.suggestion}</code>
                      <p className="text-[11px] text-slate-500 mt-0.5">{t.note}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
